<template>
  <!-- Overlay + modal (sans Bootstrap JS; piloté par v-if côté parent) -->
  <div class="modal fade show d-block" tabindex="-1" role="dialog" aria-modal="true"
       style="background-color: rgba(0,0,0,0.5);">
    <div class="modal-dialog" role="document">
      <div class="modal-content">
        <ValidationObserver ref="observer" v-slot="{ invalid }">
          <form @submit.prevent="submitForm">
            <div class="modal-header bg-primary text-white">
              <h6 class="modal-title fw-bold">
                {{ isEdit ? 'Modifier l’unité de vente' : 'Ajouter une unité de vente' }}
              </h6>
              <button type="button" class="btn-close" aria-label="Fermer" @click="$emit('close')"></button>
            </div>

            <div class="modal-body">
              <!-- Unité -->
              <div class="form-group">
                <ValidationProvider name="Unité" rules="required" v-slot="{ errors }">
                  <label class="form-label">Unité <span class="text-danger">*</span></label>
                  <multi-select
                      v-model="form.unit"
                      :options="filteredUnits"
                      label="none"
                      track-by="code"
                      :searchable="true"
                      :custom-label="unitOptionLabel"
                      deselect-label="Sélectionné"
                      select-label=""
                      placeholder="Sélectionner"
                  >
                    <template slot="singleLabel" slot-scope="{ option }">
                      <span>{{ unitOptionLabel(option) }}</span>
                    </template>
                    <template slot="option" slot-scope="{ option }">
                      <span>{{ unitOptionLabel(option) }}</span>
                    </template>
                  </multi-select>
                  <small class="text-danger">{{ errors[0] }}</small>
                </ValidationProvider>
              </div>

              <!-- Contenu -->
              <div class="form-group">
                <ValidationProvider name="Contenu" rules="required|min_value:0.000001" v-slot="{ errors }">
                  <label class="form-label">
                    Contenu (combien de <strong>{{ baseUnitLabel }}</strong> par unité)
                    <span class="text-danger">*</span>
                  </label>
                  <input
                      type="number"
                      step="0.000001"
                      min="0"
                      v-model.number="form.contentQty"
                      class="form-control"
                      placeholder="Ex: 12"
                      autocomplete="off"
                  />
                  <small class="text-danger">{{ errors[0] }}</small>
                </ValidationProvider>
                <small class="text-muted">Ex: 1 PACK = 12 {{ baseUnitLabel }} ⇒ saisir 12.</small>
              </div>

              <!-- Prix -->
              <div class="form-group">
                <ValidationProvider name="Prix de vente" rules="required|min_value:0.01" v-slot="{ errors }">
                  <label class="form-label">Prix de vente <span class="text-danger">*</span></label>
                  <input
                      type="number"
                      step="0.01"
                      min="0.01"
                      v-model.number="form.salePrice"
                      class="form-control"
                      placeholder="0.00"
                      autocomplete="off"
                  />
                  <small class="text-danger">{{ errors[0] }}</small>
                </ValidationProvider>
              </div>

              <!-- Par défaut -->
              <div class="form-check">
                <input id="chkDefaultUnit" class="form-check-input" type="checkbox" v-model="form.defaultUnit" />
                <label class="form-check-label" for="chkDefaultUnit">Définir comme unité par défaut</label>
              </div>
            </div>

            <div class="modal-footer">
              <button type="button" class="btn btn-secondary" @click="$emit('close')">Annuler</button>
              <button type="submit" class="btn btn-primary" :disabled="invalid">
                {{ isEdit ? 'Mettre à jour' : 'Enregistrer' }}
              </button>
            </div>
          </form>
        </ValidationObserver>
      </div>
    </div>
  </div>
</template>

<script>
  export default {
    name: 'SellingUnitsModal',
    props: {
      mode: { type: String, default: 'create' },
      allUnits: { type: Array, required: true },
      initial: { type: Object, default: null },
      baseUnit: { type: String, default: '' },
      usedUnitCodes: { type: Array, default: () => [] }          // code de l'unité de base (ex: 'PIECE')
    },
    data() {
      return {
        form: {
          unit: null,          // objet unité sélectionné
          contentQty: null,
          salePrice: null,
          defaultUnit: false
        }
      };
    },
    computed: {
      isEdit() { return this.mode === 'edit'; },
      baseUnitLabel() {
        const u = this.allUnits.find(u => u.code === this.baseUnit);
        return u ? (u.label || u.description || u.code) : (this.baseUnit || 'unité');
      },
      filteredUnits() {
        const currentCode = this.initial?.unitCode?.code || this.initial?.unitCode || null;
        return this.allUnits.filter(u => {
          // autoriser le code courant en mode edit, sinon exclure s’il est déjà utilisé
          if (this.isEdit && u.code === currentCode) return true;
          return !this.usedUnitCodes.includes(u.code);
        });
      }
    },
    watch: {
      // Pré-remplir le formulaire quand initial change
      initial: {
        immediate: true,
        handler(v) {
          if (!v) {
            this.form = { unit: null, contentQty: null, salePrice: null, defaultUnit: false };
            return;
          }
          const unitObj = this.allUnits.find(u => u.code === (v.unitCode?.code || v.unitCode));
          this.form = {
            unit: unitObj || null,
            contentQty: v.contentQty ?? null,
            salePrice: v.salePrice ?? null,
            defaultUnit: !!v.defaultUnit
          };
        }
      }
    },
    methods: {
      unitOptionLabel(o) {
        return o ? `${o.code} - ${o.label || o.description || ''}` : '';
      },
      async submitForm() {
        const ok = await this.$refs.observer.validate();
        if (!ok) return;

        const payload = {
          unitCode: this.form.unit?.code || '',
          contentQty: this.form.contentQty,
          salePrice: this.form.salePrice,
          defaultUnit: !!this.form.defaultUnit
        };
        if (!payload.unitCode) return;

        // Le modal reste "stateless": on émet l'intention et on laisse le parent gérer la liste.
        if (this.isEdit) {
          this.$emit('update', payload);
        } else {
          this.$emit('save', payload);
        }
      }
    }
  };
</script>
